/**
 * Sorrel House · header (sections/sh-header.liquid).
 * <sh-header> :
 * - panneau de recherche sous le header : ouvert / ferme par [data-sh-search-toggle], focus sur le
 *   champ, Echap et clic dehors ferment ; suggestions predictives (produits + collections) via
 *   routes.predictive_search_url, debounce 250 ms, SANS prix (devise boutique, Blueprint §7.33),
 *   lien « See all results ». La loupe reste un <a href=/search> : sans JS, la page de recherche.
 * - volet menu (<sh-sheet> de sh-ui.js) : ferme quand on repasse au-dessus de 1160 px.
 * - aria-expanded du bouton panier suivi sur theme-drawer:open / theme-drawer:close.
 * Module autonome, aucune transition.
 */
const DESKTOP_QUERY = '(min-width: 1160px)';

class ShHeader extends HTMLElement {
  #abort = null;
  #desktop = window.matchMedia(DESKTOP_QUERY);
  #timer = 0;
  #seq = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.panel = this.querySelector('.sh-hdr__search-panel');
    this.input = this.querySelector('[data-sh-search-input]');
    this.results = this.querySelector('[data-sh-search-results]');

    this.querySelectorAll('[data-sh-search-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.toggleSearch(); }, { signal });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel && !this.panel.hidden) { this.toggleSearch(false); }
    }, { signal });
    document.addEventListener('click', (e) => {
      if (!this.panel || this.panel.hidden) return;
      if (!this.contains(/** @type {Node} */ (e.target))) this.toggleSearch(false);
    }, { signal });
    this.input?.addEventListener('input', () => {
      clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => this.#suggest(), 250);
    }, { signal });

    this.#desktop.addEventListener('change', (e) => {
      if (!e.matches) return;
      const sheet = document.getElementById(this.dataset.sheet || '');
      // @ts-ignore close() est fourni par <sh-sheet> (sh-ui.js)
      if (sheet && typeof sheet.close === 'function') sheet.close();
    }, { signal });

    const cartBtn = this.querySelector('[data-sh-cart-trigger]');
    if (cartBtn) {
      document.addEventListener('theme-drawer:open', () => cartBtn.setAttribute('aria-expanded', 'true'), { signal });
      document.addEventListener('theme-drawer:close', () => cartBtn.setAttribute('aria-expanded', 'false'), { signal });
    }
  }

  disconnectedCallback() { this.#abort?.abort(); }

  toggleSearch(force) {
    if (!this.panel) return;
    const open = typeof force === 'boolean' ? force : this.panel.hidden;
    this.panel.hidden = !open;
    this.querySelectorAll('.sh-hdr__search').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) {
      requestAnimationFrame(() => this.input?.focus());
    } else {
      /** @type {HTMLElement | null} */ (this.querySelector('.sh-hdr__search'))?.focus({ preventScroll: true });
    }
  }

  async #suggest() {
    if (!this.input || !this.results) return;
    const q = this.input.value.trim();
    const seq = ++this.#seq;
    if (q.length < 2) { this.results.hidden = true; this.results.innerHTML = ''; return; }
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base = this.results.dataset.url || `${root}search/suggest`;
      const url = `${base}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=5&resources[options][unavailable_products]=last`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok || seq !== this.#seq) return;
      const data = await res.json();
      const r = (data && data.resources && data.resources.results) || {};
      this.#render(q, r.products || [], r.collections || [], root);
    } catch (e) {
      /* la recherche classique (Entree) reste disponible */
    }
  }

  #render(q, products, collections, root) {
    if (!this.results) return;
    const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const all = `${root}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
    if (!products.length && !collections.length) {
      this.results.innerHTML = `<p class="sh-hdr__sr-empty">Nothing matched “${esc(q)}”.</p>`;
    } else {
      const thumb = (src) => (src ? `<img class="sh-hdr__sr-thumb" src="${esc(src + (src.includes('?') ? '&' : '?') + 'width=96')}" alt="" width="48" height="48" loading="lazy">` : '<span class="sh-hdr__sr-thumb"></span>');
      const cols = collections.slice(0, 2).map((c) => `<li class="sh-hdr__sr-item sh-hdr__sr-item--col"><a href="${esc(c.url)}"><span class="sh-hdr__sr-kicker">Collection</span><span class="sh-hdr__sr-name">${esc(c.title)}</span></a></li>`).join('');
      const items = products.slice(0, 5).map((p) => `<li class="sh-hdr__sr-item"><a href="${esc(p.url)}">${thumb(p.image || (p.featured_image && p.featured_image.url))}<span class="sh-hdr__sr-name">${esc(p.title)}</span></a></li>`).join('');
      this.results.innerHTML = `<ul class="sh-hdr__sr-list" role="list">${cols}${items}</ul><a class="sh-hdr__sr-all" href="${all}">See all results</a>`;
    }
    this.results.hidden = false;
  }
}

if (!customElements.get('sh-header')) customElements.define('sh-header', ShHeader);
